import { querySettings } from '@core/app/pages/query-settings'
import { queryPage } from '@core/app/pages/query-page'
import { getPageView } from '@core/analytics/pages'
import { PageTypes } from '@core/constants/pages'
import type { Corridor } from '@core/types/imt'
import { MONEY_TRANSFER } from '@gifsa/language-utils/routes'

export default async function loadPageData() {
  const route = useRoute()
  const nuxtApp = useNuxtApp()
  const { $lang, $i18next } = nuxtApp

  const { data, error } = await useAsyncData(`pageData:${route.path}`, async () => {
    const { countryFrom, countryTo, currencyFrom, currencyTo, amount } = processIMTParams(
      route.params,
    )
    if (!currencyFrom || !currencyTo) {
      throw new NotFoundError(`Invalid currency params ${JSON.stringify(route.params)}`)
    }
    const params = { countryFrom, countryTo, currencyFrom, currencyTo }

    const prefix = MONEY_TRANSFER.find((r) => r.lang === $lang)?.url
    if (!prefix) {
      throw new NotFoundError(`Unsupported language: ${$lang}`)
    }
    const slug = `${prefix}${countryFrom.slug}/${countryTo.slug}`
    const pattern = `${prefix}%COUNTRY_FROM_SLUG%/%COUNTRY_TO_SLUG%`

    const [{ navigation, footer, footerContent }, { body, page }, historicalData] =
      await Promise.all([
        querySettings(nuxtApp),
        queryPage(nuxtApp, slug, pattern),
        queryHistoricalStats(nuxtApp, params),
        $i18next.loadNamespaces(['provider_messages']),
      ])

    const corridor: Corridor = {
      ...params,
      amount,
      defaultAmount: historicalData?.mostPopularAmount || 1000,
    }

    const alternateUrls = getActiveLanguages().map((language) => ({
      language,
      slug: getIMTLiveUrl(corridor, language),
    }))

    return {
      navigation,
      footer,
      body: [...body, ...(footerContent ?? [])],
      page,
      corridor,
      alternateUrls,
      historicalData: {
        cheapestToBank: historicalData?.cheapestStdBank ?? null,
        cheapestToCash: historicalData?.cheapestStdCash ?? null,
        cheapestToCard: historicalData?.cheapestStdCard ?? null,
        cheapestToWallet: historicalData?.cheapestStdWallet ?? null,
      },
    }
  })

  if (!data.value) {
    throw createAsyncDataError(error)
  }

  if (import.meta.client) {
    const { page, corridor } = data.value
    await nuxtApp.$track(
      getPageView(route, {
        type: PageTypes.TRANSFER,
        page: { tags: page.tags || [], context: page.context },
        corridor,
      }),
    )
  }

  return data.value
}
